import { useEffect, useMemo, useRef, useState } from 'react';
import { Check, FileUp, Info } from 'lucide-react';
import { toast } from 'react-toastify';
import { Line, LineChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { MultiSeriesComparisonChart, WaveformChart } from '../respiratory-muscle/RespiratoryMuscleCharts';
import {
  analyzeEnduranceCsv,
  buildBlockComparisonData,
  buildEnduranceInterpretation,
  buildWavePoints,
  formatSecondsToTlim,
  getRespiratoryEnduranceProtocolLines,
  parseTlimToSeconds,
  RESPIRATORY_ENDURANCE_PRESETS,
  sanitizeMetricsForStorage,
  sanitizePlotBreathsForStorage,
  summarizeRespiratoryEndurancePayload,
  toFiniteNumber,
  toLocalDateTimeKey,
  type EnduranceAnalysis,
  type RespiratoryCsvFileMeta,
  type RespiratoryEnduranceResultPayload,
  type RespiratoryEnduranceProtocol,
  type RespiratoryEndurancePreset,
  type RespiratoryMusclePatientContext,
} from '../respiratory-muscle/respiratoryMuscleDomain';

interface RespiratoryEnduranceTestProps {
  initial?: (RespiratoryEnduranceResultPayload & { chartImages?: Record<string, string> }) | null;
  patientContext?: RespiratoryMusclePatientContext;
  onCancel?: () => void;
  onNext: (result: RespiratoryEnduranceResultPayload & { chartImages?: Record<string, string> }) => void;
}

export type { RespiratoryEnduranceResultPayload };

const exportElement = async (element: HTMLElement): Promise<string> => {
  const html2canvas = (await import('html2canvas')).default;
  const previousWidth = element.style.width;
  const previousMaxWidth = element.style.maxWidth;
  element.style.width = '760px';
  element.style.maxWidth = 'none';
  try {
    await new Promise<void>((resolve) => requestAnimationFrame(() => requestAnimationFrame(() => resolve())));
    const canvas = await html2canvas(element, {
      scale: 2,
      backgroundColor: '#ffffff',
      logging: false,
      useCORS: true,
      allowTaint: true,
      width: 760,
      windowWidth: 900,
    });
    return canvas.toDataURL('image/png');
  } finally {
    element.style.width = previousWidth;
    element.style.maxWidth = previousMaxWidth;
  }
};

const defaultProtocol: RespiratoryEnduranceProtocol = {
  entryMode: 'manual',
  modality: 'threshold',
  preset: 'custom',
  thresholdProtocol: 'constant',
  referenceBasis: 'pimax',
  pacing: 'spontaneous',
  familiarization: null,
  attemptNumber: 1,
};

const buildInitialAnalysis = (initial?: RespiratoryEnduranceResultPayload | null): EnduranceAnalysis | null => {
  if (!initial?.metrics?.length || !initial.plotBreaths?.length) return null;
  return {
    waveforms: initial.plotBreaths,
    metrics: initial.metrics,
    plotBreaths: initial.plotBreaths,
    blocks: initial.blocks ?? [],
    targetResistance: initial.summary?.targetResistance ?? null,
    breathsAfterWarmup: initial.summary?.breathsAfterWarmup ?? initial.metrics.length,
    firstBlock: initial.summary?.firstBlock ?? initial.blocks?.[0] ?? null,
    lastBlock: initial.summary?.lastBlock ?? initial.blocks?.[(initial.blocks?.length ?? 1) - 1] ?? null,
    reductions: initial.summary?.reductions ?? {},
  };
};

export default function RespiratoryEnduranceTest({ initial = null, patientContext, onCancel, onNext }: RespiratoryEnduranceTestProps) {
  const initialAnalysis = buildInitialAnalysis(initial);
  const [analysis, setAnalysis] = useState<EnduranceAnalysis | null>(initialAnalysis);
  const [selectedBreath, setSelectedBreath] = useState<number | null>(initialAnalysis?.plotBreaths[0]?.breathNumber ?? null);
  const [fileMeta, setFileMeta] = useState<RespiratoryCsvFileMeta | null>(initial?.file ?? null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [curveView, setCurveView] = useState<'comparison' | 'individual'>('comparison');
  const [tlimInput, setTlimInput] = useState<string>(initial?.summary?.tlimFormatted ?? '');
  const [tlimError, setTlimError] = useState<string | null>(null);
  const [exportingCharts, setExportingCharts] = useState(false);
  const [protocol, setProtocol] = useState<RespiratoryEnduranceProtocol>({
    ...defaultProtocol,
    ...(initial?.protocol ?? {}),
    entryMode: initial?.file ? 'csv' : initial?.protocol?.entryMode ?? 'manual',
  });
  const chartRefs = useRef<Record<string, HTMLDivElement | null>>({});

  const tlimSeconds = useMemo(() => parseTlimToSeconds(tlimInput), [tlimInput]);
  const selectedWaveform = useMemo(() => {
    if (!analysis) return null;
    if (selectedBreath == null) return analysis.plotBreaths[0] ?? null;
    return analysis.plotBreaths.find((plot) => plot.breathNumber === selectedBreath) ?? analysis.plotBreaths[0] ?? null;
  }, [analysis, selectedBreath]);
  const chartData = useMemo(() => buildWavePoints(selectedWaveform), [selectedWaveform]);
  const blockChartData = useMemo(
    () => analysis?.blocks.map((block) => ({ block: block.index, flow: block.meanPeakFlow, volume: block.meanPeakVolume, work: block.meanTotalWork })) ?? [],
    [analysis],
  );
  const blockComparison = useMemo(() => (analysis ? buildBlockComparisonData(analysis.plotBreaths, analysis.blocks) : null), [analysis]);
  const calculatedTargetLoad = useMemo(() => {
    if (protocol.referenceBasis === 'absolute') return toFiniteNumber(protocol.targetLoad);
    const reference = toFiniteNumber(protocol.referenceValue);
    const percent = toFiniteNumber(protocol.targetPercent);
    if (protocol.modality === 'threshold' && protocol.thresholdProtocol === 'constant' && reference != null && percent != null) {
      return Number(((reference * percent) / 100).toFixed(1));
    }
    return toFiniteNumber(protocol.targetLoad) ?? analysis?.targetResistance ?? null;
  }, [analysis?.targetResistance, protocol.modality, protocol.referenceBasis, protocol.referenceValue, protocol.targetLoad, protocol.targetPercent, protocol.thresholdProtocol]);
  const calculatedTargetVentilation = useMemo(() => {
    const informed = toFiniteNumber(protocol.targetVentilation);
    if (informed != null) return informed;
    const mvv = toFiniteNumber(protocol.mvv);
    const percent = toFiniteNumber(protocol.targetMvvPercent);
    return mvv != null && percent != null ? Number(((mvv * percent) / 100).toFixed(1)) : null;
  }, [protocol.mvv, protocol.targetMvvPercent, protocol.targetVentilation]);
  const protocolValidationMessage = useMemo(() => {
    if (protocol.modality === 'threshold' && protocol.thresholdProtocol === 'constant' && calculatedTargetLoad == null) {
      return 'Informe a carga absoluta ou o valor de referência e a porcentagem da carga constante.';
    }
    if (protocol.modality === 'threshold' && protocol.thresholdProtocol !== 'constant') {
      if (toFiniteNumber(protocol.startingPercent) == null || toFiniteNumber(protocol.incrementPercent) == null || toFiniteNumber(protocol.stageDurationSeconds) == null) {
        return 'Informe carga inicial, incremento e duração dos estágios do protocolo incremental.';
      }
    }
    if (protocol.modality === 'hyperpnea' && calculatedTargetVentilation == null) {
      return 'Informe a ventilação alvo ou a VVM e sua porcentagem.';
    }
    if (protocol.modality === 'time_trial' && toFiniteNumber(protocol.timeTrialDurationSeconds) == null) {
      return 'Informe a duração programada do teste de tempo fixo.';
    }
    return null;
  }, [calculatedTargetLoad, calculatedTargetVentilation, protocol.incrementPercent, protocol.modality, protocol.stageDurationSeconds, protocol.startingPercent, protocol.thresholdProtocol, protocol.timeTrialDurationSeconds]);

  const summaryPreview = useMemo<RespiratoryEnduranceResultPayload | null>(() => {
    if (tlimSeconds == null) return null;
    if (protocol.entryMode === 'csv' && !analysis) return null;
    const normalizedProtocol: RespiratoryEnduranceProtocol = {
      ...protocol,
      targetLoad: calculatedTargetLoad,
      targetVentilation: calculatedTargetVentilation,
      entryMode: analysis ? 'csv' : 'manual',
    };
    return {
      summary: {
        tlimSeconds,
        tlimFormatted: formatSecondsToTlim(tlimSeconds),
        targetResistance: calculatedTargetLoad ?? analysis?.targetResistance ?? null,
        breathsAfterWarmup: analysis?.breathsAfterWarmup ?? 0,
        firstBlock: analysis?.firstBlock ?? null,
        lastBlock: analysis?.lastBlock ?? null,
        reductions: analysis?.reductions ?? {},
      },
      protocol: normalizedProtocol,
      blocks: analysis?.blocks ?? [],
      metrics: analysis ? sanitizeMetricsForStorage(analysis.metrics) : [],
      plotBreaths: analysis ? sanitizePlotBreathsForStorage(analysis.plotBreaths) : [],
      file: analysis ? fileMeta ?? undefined : undefined,
      patientContext,
    };
  }, [analysis, calculatedTargetLoad, calculatedTargetVentilation, fileMeta, patientContext, protocol, tlimSeconds]);
  const interpretation = useMemo(() => buildEnduranceInterpretation(summaryPreview), [summaryPreview]);
  const liveSummary = summarizeRespiratoryEndurancePayload(summaryPreview);
  const protocolPreviewLines = useMemo(() => getRespiratoryEnduranceProtocolLines(summaryPreview?.protocol), [summaryPreview?.protocol]);

  const handleTlimChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    let value = event.target.value.replace(/[^\d:]/g, '');
    if (value.length === 2 && !value.includes(':') && tlimInput.length < value.length) value = `${value}:`;
    if (value.length > 5) value = value.slice(0, 5);
    setTlimInput(value);
    setTlimError(value && !parseTlimToSeconds(value) && value.length >= 4 ? 'Formato inválido. Use MM:SS.' : null);
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setLoading(true);
    setError(null);
    const reader = new FileReader();
    reader.onload = () => {
      try {
        const parsed = analyzeEnduranceCsv(typeof reader.result === 'string' ? reader.result : '');
        setAnalysis(parsed);
        setSelectedBreath(parsed.plotBreaths[0]?.breathNumber ?? null);
        setCurveView('comparison');
        setFileMeta({ name: file.name, size: file.size, importedAt: toLocalDateTimeKey(new Date()) });
        setProtocol((current) => ({ ...current, entryMode: 'csv', targetLoad: parsed.targetResistance ?? current.targetLoad }));
      } catch (caught) {
        console.error('Erro ao processar endurance respiratório:', caught);
        setAnalysis(null);
        setSelectedBreath(null);
        setError(caught instanceof Error ? caught.message : 'Não foi possível processar o arquivo.');
      } finally {
        setLoading(false);
        event.target.value = '';
      }
    };
    reader.onerror = () => {
      setLoading(false);
      setError('Erro ao ler o arquivo.');
      event.target.value = '';
    };
    reader.readAsText(file, 'utf-8');
  };

  const exportChartsAsImages = async (): Promise<Record<string, string>> => {
    if (!analysis) return {};
    setExportingCharts(true);
    const images: Record<string, string> = {};
    const chartKeys = blockComparison
      ? ['comparison_pressure', 'comparison_flow', 'comparison_volume', 'comparison_work']
      : ['individual_pressure', 'individual_flow', 'individual_volume', 'individual_work'];
    if (blockChartData.length > 1) chartKeys.push('block_evolution');

    try {
      for (const key of chartKeys) {
        const element = chartRefs.current[key];
        if (element) images[key] = await exportElement(element);
      }
      return images;
    } catch (caught) {
      console.error('Erro ao exportar gráficos de endurance respiratório:', caught);
      return {};
    } finally {
      setExportingCharts(false);
    }
  };

  const handleFinish = async () => {
    if (protocolValidationMessage) {
      toast.error(protocolValidationMessage);
      return;
    }
    if (!summaryPreview) {
      toast.error('Informe o tempo até a interrupção para concluir.');
      return;
    }
    const chartImages = await exportChartsAsImages();
    onNext({
      ...summaryPreview,
      ...(Object.keys(chartImages).length ? { chartImages } : {}),
      finishedAt: toLocalDateTimeKey(new Date()),
    });
  };

  const updateProtocol = <K extends keyof RespiratoryEnduranceProtocol>(key: K, value: RespiratoryEnduranceProtocol[K]) => {
    setProtocol((current) => ({ ...current, [key]: value }));
  };

  const applyPreset = (preset: RespiratoryEndurancePreset) => {
    const selected = RESPIRATORY_ENDURANCE_PRESETS[preset];
    setProtocol((current) => ({
      ...current,
      ...selected.defaults,
      preset,
      entryMode: current.entryMode,
      modality: current.modality,
    }));
  };

  return (
    <div className="space-y-4">
      <div className="flex items-start gap-2 rounded-xl border border-slate-200 bg-white p-4 text-slate-700 shadow-sm">
        <Info className="mt-0.5 h-5 w-5" />
        <div>
          <p className="font-semibold text-slate-900">Endurance muscular respiratório</p>
          <p className="text-sm">Escolha a modalidade e registre o protocolo realmente aplicado. Arquivos do BreatheLink são opcionais.</p>
        </div>
      </div>

      <section className="space-y-4 rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
        <div>
          <div className="text-xs font-bold uppercase tracking-[0.18em] text-slate-400">Modalidade</div>
          <div className="mt-3 grid gap-2 md:grid-cols-4">
            {([
              ['threshold', 'Carga limiar'],
              ['hyperpnea', 'Hiperventilação isocápnica'],
              ['time_trial', 'Tempo fixo'],
              ['custom', 'Personalizado'],
            ] as const).map(([value, label]) => (
              <button
                key={value}
                type="button"
                onClick={() => updateProtocol('modality', value)}
                className={`rounded-xl border px-3 py-3 text-left text-sm font-semibold ${protocol.modality === value ? 'border-sky-500 bg-sky-50 text-sky-900' : 'border-slate-200 bg-white text-slate-700'}`}
              >
                {label}
              </button>
            ))}
          </div>
        </div>

        <label className="block text-sm font-medium text-slate-700">
          População / finalidade (preset)
          <select className="mt-1 w-full rounded-lg border border-slate-300 bg-white px-3 py-2" value={protocol.preset} onChange={(event) => applyPreset(event.target.value as RespiratoryEndurancePreset)}>
            {Object.entries(RESPIRATORY_ENDURANCE_PRESETS).map(([key, preset]) => <option key={key} value={key}>{preset.label}</option>)}
          </select>
          <span className="mt-1 block text-xs font-normal text-slate-500">{RESPIRATORY_ENDURANCE_PRESETS[protocol.preset].description} A modalidade escolhida acima é preservada.</span>
        </label>
        {protocol.preset === 'custom' && <TextField label="População / finalidade do protocolo" value={protocol.purpose ?? ''} onChange={(value) => updateProtocol('purpose', value || null)} />}
        <TextField label="Fonte ou nome do protocolo (opcional)" value={protocol.source ?? ''} onChange={(value) => updateProtocol('source', value || null)} />

        {protocol.modality === 'threshold' && (
          <div className="grid gap-3 md:grid-cols-3">
            <SelectField label="Tipo de protocolo" value={protocol.thresholdProtocol ?? 'constant'} onChange={(value) => updateProtocol('thresholdProtocol', value as RespiratoryEnduranceProtocol['thresholdProtocol'])} options={[['constant', 'Carga constante'], ['incremental', 'Incremental completo'], ['powerbreathe_incremental', 'Incremental PowerBreathe']]} />
            <SelectField label="Base da carga" value={protocol.referenceBasis ?? 'pimax'} onChange={(value) => updateProtocol('referenceBasis', value as RespiratoryEnduranceProtocol['referenceBasis'])} options={[['pimax', 'PImax'], ['ppeak', 'Ppeak'], ['pthmax', 'Pthmax'], ['absolute', 'Carga absoluta']]} />
            {protocol.referenceBasis !== 'absolute' && <NumberField label={`Valor de referência (${protocol.referenceBasis === 'pimax' ? 'PImax' : protocol.referenceBasis === 'ppeak' ? 'Ppeak' : 'Pthmax'})`} unit="cmH2O" value={protocol.referenceValue} onChange={(value) => updateProtocol('referenceValue', value)} />}
            {protocol.thresholdProtocol === 'constant' && protocol.referenceBasis !== 'absolute' && <NumberField label="Carga alvo" unit="%" value={protocol.targetPercent} onChange={(value) => updateProtocol('targetPercent', value)} />}
            {protocol.referenceBasis === 'absolute' && <NumberField label="Carga alvo" unit="cmH2O" value={protocol.targetLoad} onChange={(value) => updateProtocol('targetLoad', value)} />}
            {protocol.thresholdProtocol !== 'constant' && <NumberField label="Carga inicial" unit="%" value={protocol.startingPercent} onChange={(value) => updateProtocol('startingPercent', value)} />}
            {protocol.thresholdProtocol !== 'constant' && <NumberField label="Incremento por estágio" unit="%" value={protocol.incrementPercent} onChange={(value) => updateProtocol('incrementPercent', value)} />}
            {protocol.thresholdProtocol !== 'constant' && <NumberField label="Duração do estágio" unit="segundos" value={protocol.stageDurationSeconds} onChange={(value) => updateProtocol('stageDurationSeconds', value)} />}
            {protocol.thresholdProtocol !== 'constant' && <NumberField label="Carga máxima atingida" unit="cmH2O" value={protocol.targetLoad} onChange={(value) => updateProtocol('targetLoad', value)} />}
            {calculatedTargetLoad != null && protocol.thresholdProtocol === 'constant' && <ReadOnlyField label="Carga calculada" value={`${calculatedTargetLoad.toFixed(1)} cmH2O`} />}
          </div>
        )}

        {protocol.modality === 'hyperpnea' && (
          <div className="grid gap-3 md:grid-cols-3">
            <NumberField label="Ventilação voluntária máxima" unit="L/min" value={protocol.mvv} onChange={(value) => updateProtocol('mvv', value)} />
            <NumberField label="Alvo da VVM" unit="%" value={protocol.targetMvvPercent} onChange={(value) => updateProtocol('targetMvvPercent', value)} />
            <NumberField label="Ventilação alvo" unit="L/min" value={protocol.targetVentilation} onChange={(value) => updateProtocol('targetVentilation', value)} />
            {calculatedTargetVentilation != null && protocol.targetVentilation == null && <ReadOnlyField label="Ventilação calculada" value={`${calculatedTargetVentilation.toFixed(1)} L/min`} />}
            <NumberField label="Frequência respiratória alvo" unit="irpm" value={protocol.targetRespiratoryRate} onChange={(value) => updateProtocol('targetRespiratoryRate', value)} />
            <NumberField label="Volume corrente alvo" unit="L" value={protocol.targetTidalVolume} onChange={(value) => updateProtocol('targetTidalVolume', value)} />
            <SelectField label="Controle de isocapnia" value={protocol.isocapnia == null ? '' : protocol.isocapnia ? 'yes' : 'no'} onChange={(value) => updateProtocol('isocapnia', value === '' ? null : value === 'yes')} options={[['', 'Não informado'], ['yes', 'Sim'], ['no', 'Não']]} />
          </div>
        )}

        {(protocol.modality === 'time_trial' || protocol.modality === 'custom') && (
          <div className="grid gap-3 md:grid-cols-3">
            <NumberField label="Duração programada" unit="segundos" value={protocol.timeTrialDurationSeconds} onChange={(value) => updateProtocol('timeTrialDurationSeconds', value)} />
            <NumberField label="Carga aplicada" unit="cmH2O" value={protocol.targetLoad} onChange={(value) => updateProtocol('targetLoad', value)} />
            <NumberField label="Resultado no tempo fixo" value={protocol.timeTrialOutcome} onChange={(value) => updateProtocol('timeTrialOutcome', value)} />
            <SelectField label="Unidade do resultado" value={protocol.timeTrialOutcomeUnit ?? ''} onChange={(value) => updateProtocol('timeTrialOutcomeUnit', (value || null) as RespiratoryEnduranceProtocol['timeTrialOutcomeUnit'])} options={[['', 'Não informada'], ['respiracoes', 'Respirações'], ['trabalho', 'Trabalho'], ['ventilacao', 'Ventilação']]} />
          </div>
        )}
      </section>

      <section className="grid gap-4 rounded-2xl border border-slate-200 bg-white p-5 shadow-sm md:grid-cols-2">
        <div>
          <div className="text-xs font-bold uppercase tracking-[0.18em] text-slate-400">Origem dos dados</div>
          <div className="mt-2 inline-flex rounded-lg border border-slate-200 p-1 text-sm">
            <button
              type="button"
              onClick={() => {
                updateProtocol('entryMode', 'manual');
                setAnalysis(null);
                setSelectedBreath(null);
                setFileMeta(null);
              }}
              className={`rounded-md px-3 py-1.5 ${protocol.entryMode === 'manual' ? 'bg-slate-900 text-white' : 'text-slate-600'}`}
            >Manual</button>
            <button type="button" onClick={() => updateProtocol('entryMode', 'csv')} className={`rounded-md px-3 py-1.5 ${protocol.entryMode === 'csv' ? 'bg-slate-900 text-white' : 'text-slate-600'}`}>BreatheLink CSV</button>
          </div>
        </div>
        <label className="block text-sm font-medium text-slate-700">
          Tempo até interrupção (Tlim)
          <div className="mt-1 flex items-center gap-3">
            <input className={`w-32 rounded-lg border px-3 py-2 font-mono text-lg ${tlimError ? 'border-red-500 bg-red-50' : 'border-slate-300'}`} value={tlimInput} onChange={handleTlimChange} placeholder="MM:SS" maxLength={5} />
            <span className="text-xs text-slate-500">{tlimSeconds != null ? `${tlimSeconds} segundos` : 'Ex.: 08:30'}</span>
          </div>
          {tlimError && <span className="mt-1 block text-xs text-red-600">{tlimError}</span>}
        </label>
      </section>

      {protocol.entryMode === 'csv' && (
        <label className="flex cursor-pointer flex-col gap-2 rounded-xl border border-dashed border-slate-300 bg-white p-4 text-sm text-slate-700 hover:bg-slate-50">
          <span className="flex items-center gap-2 font-medium"><FileUp className="h-4 w-4" />Selecionar arquivo CSV</span>
          <input type="file" accept=".csv" className="hidden" onChange={handleFileChange} />
          <span className="text-xs text-slate-500">As quatro primeiras respirações são desconsideradas e as demais são agrupadas em blocos de cinco.</span>
          {fileMeta && <span className="text-xs text-emerald-700">Arquivo carregado: {fileMeta.name} ({Math.round(fileMeta.size / 1024)} KB)</span>}
        </label>
      )}

      {loading && <div className="text-sm text-blue-600">Processando arquivo...</div>}
      {error && <div className="text-sm text-red-600">Erro: {error}</div>}

      <details className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
        <summary className="cursor-pointer font-semibold text-slate-800">Execução, sintomas e sinais vitais</summary>
        <div className="mt-4 grid gap-3 md:grid-cols-4">
          <SelectField label="Pacing" value={protocol.pacing ?? 'spontaneous'} onChange={(value) => updateProtocol('pacing', value as RespiratoryEnduranceProtocol['pacing'])} options={[['spontaneous', 'Espontâneo'], ['metronome', 'Metrônomo']]} />
          <NumberField label="FR alvo" unit="irpm" value={protocol.targetRespiratoryRate} onChange={(value) => updateProtocol('targetRespiratoryRate', value)} />
          <NumberField label="FR observada" unit="irpm" value={protocol.observedRespiratoryRate} onChange={(value) => updateProtocol('observedRespiratoryRate', value)} />
          <TextField label="Postura" value={protocol.posture ?? ''} onChange={(value) => updateProtocol('posture', value || null)} />
          <SelectField label="Familiarização" value={protocol.familiarization == null ? '' : protocol.familiarization ? 'yes' : 'no'} onChange={(value) => updateProtocol('familiarization', value === '' ? null : value === 'yes')} options={[['', 'Não informada'], ['yes', 'Sim'], ['no', 'Não']]} />
          <NumberField label="Tentativa" value={protocol.attemptNumber} onChange={(value) => updateProtocol('attemptNumber', value)} />
          <NumberField label="Borg dispneia pré" value={protocol.borgDyspneaPre} onChange={(value) => updateProtocol('borgDyspneaPre', value)} />
          <NumberField label="Borg dispneia pós" value={protocol.borgDyspneaPost} onChange={(value) => updateProtocol('borgDyspneaPost', value)} />
          <NumberField label="Borg fadiga pré" value={protocol.borgFatiguePre} onChange={(value) => updateProtocol('borgFatiguePre', value)} />
          <NumberField label="Borg fadiga pós" value={protocol.borgFatiguePost} onChange={(value) => updateProtocol('borgFatiguePost', value)} />
          <NumberField label="SpO2 pré" unit="%" value={protocol.spo2Pre} onChange={(value) => updateProtocol('spo2Pre', value)} />
          <NumberField label="SpO2 pós" unit="%" value={protocol.spo2Post} onChange={(value) => updateProtocol('spo2Post', value)} />
          <NumberField label="FC pré" unit="bpm" value={protocol.heartRatePre} onChange={(value) => updateProtocol('heartRatePre', value)} />
          <NumberField label="FC pós" unit="bpm" value={protocol.heartRatePost} onChange={(value) => updateProtocol('heartRatePost', value)} />
          <div className="md:col-span-2"><TextField label="Motivo / critério de interrupção" value={protocol.stopReason ?? ''} onChange={(value) => updateProtocol('stopReason', value || null)} /></div>
          <div className="md:col-span-2"><TextField label="Observações" value={protocol.notes ?? ''} onChange={(value) => updateProtocol('notes', value || null)} /></div>
        </div>
      </details>

      {!analysis && summaryPreview && (
        <section className="grid gap-5 rounded-2xl border border-slate-200 bg-white p-5 shadow-sm md:grid-cols-3">
          <div className="text-center">
            <div className="text-xs font-bold uppercase tracking-[0.18em] text-slate-400">Tlim</div>
            <div className="mt-2 text-4xl font-black text-slate-900">{liveSummary.tlimFormatted}</div>
            {liveSummary.targetResistance != null && <div className="mt-1 text-sm text-slate-600">{liveSummary.targetResistance.toFixed(1)} cmH2O</div>}
          </div>
          <div className="md:col-span-2">
            <div className="font-semibold text-slate-900">Resumo do protocolo</div>
            <div className="mt-2 grid gap-1 text-xs leading-relaxed text-slate-600 md:grid-cols-2">
              {protocolPreviewLines.map((line, index) => <div key={`manual-endurance-protocol-${index}`}>{line}</div>)}
            </div>
            <div className="mt-3 text-sm font-semibold text-slate-800">{interpretation.title}</div>
            <div className="mt-1 text-xs text-slate-500">{interpretation.lines.join(' ')}</div>
          </div>
        </section>
      )}

      {analysis && (
        <>
          <div className="grid gap-4 md:grid-cols-4">
            <Metric label="Tlim" value={tlimSeconds != null ? formatSecondsToTlim(tlimSeconds) : '-'} emphasis />
            <Metric label="Respirações" value={String(analysis.breathsAfterWarmup)} />
            <Metric label="Resistência alvo" value={analysis.targetResistance != null ? `${analysis.targetResistance.toFixed(1)} cmH2O` : '-'} />
            <Metric label="Blocos válidos" value={String(analysis.blocks.length)} />
          </div>

          <div className="grid gap-3 md:grid-cols-3">
            <Metric label="Redução fluxo" value={liveSummary.flowReduction != null ? `${liveSummary.flowReduction.toFixed(1)}%` : '-'} />
            <Metric label="Redução volume" value={liveSummary.volumeReduction != null ? `${liveSummary.volumeReduction.toFixed(1)}%` : '-'} />
            <Metric label="Redução trabalho" value={liveSummary.workReduction != null ? `${liveSummary.workReduction.toFixed(1)}%` : '-'} />
          </div>

          <div className={`rounded-lg border p-3 text-sm ${interpretation.tone === 'good' ? 'border-emerald-200 bg-emerald-50 text-emerald-900' : interpretation.tone === 'alert' ? 'border-rose-200 bg-rose-50 text-rose-900' : interpretation.tone === 'warn' ? 'border-amber-200 bg-amber-50 text-amber-900' : 'border-slate-200 bg-slate-50 text-slate-900'}`}>
            <div className="font-semibold">Interpretação clínica: {interpretation.title}</div>
            <ul className="mt-2 list-disc space-y-1 pl-5 text-xs">{interpretation.lines.map((line, index) => <li key={`resp-endurance-interp-${index}`}>{line}</li>)}</ul>
          </div>

          <div className="space-y-3">
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-sm font-semibold text-slate-700">Curvas por respiração / blocos</span>
              <div className="inline-flex overflow-hidden rounded-md border border-slate-200 text-xs shadow-sm">
                <button type="button" className={`px-3 py-1 font-medium ${curveView === 'comparison' ? 'bg-emerald-600 text-white' : 'bg-white text-slate-700'}`} onClick={() => setCurveView('comparison')}>Blocos</button>
                <button type="button" className={`px-3 py-1 font-medium ${curveView === 'individual' ? 'bg-emerald-600 text-white' : 'bg-white text-slate-700'}`} onClick={() => setCurveView('individual')}>Respiração</button>
              </div>
            </div>

            {curveView === 'comparison' && blockComparison && (
              <div className="grid gap-4 md:grid-cols-2">
                <div ref={(element) => (chartRefs.current.comparison_pressure = element)}>
                  <MultiSeriesComparisonChart title="Pressão (cmH2O)" unit="cmH2O" data={blockComparison.pressure} seriesMeta={blockComparison.seriesMeta} />
                </div>
                <div ref={(element) => (chartRefs.current.comparison_flow = element)}>
                  <MultiSeriesComparisonChart title="Fluxo (L/s)" unit="L/s" data={blockComparison.flow} seriesMeta={blockComparison.seriesMeta} />
                </div>
                <div ref={(element) => (chartRefs.current.comparison_volume = element)}>
                  <MultiSeriesComparisonChart title="Volume (L)" unit="L" data={blockComparison.volume} seriesMeta={blockComparison.seriesMeta} />
                </div>
                <div ref={(element) => (chartRefs.current.comparison_work = element)}>
                  <MultiSeriesComparisonChart title="Trabalho acumulado" unit="cmH2O.L" data={blockComparison.work} seriesMeta={blockComparison.seriesMeta} />
                </div>
              </div>
            )}

            {curveView === 'individual' && (
              <div className="space-y-3">
                <select className="rounded border px-2 py-1 text-sm" value={selectedWaveform?.breathNumber ?? ''} onChange={(event) => setSelectedBreath(Number(event.target.value))}>
                  {analysis.plotBreaths.map((breath) => <option key={breath.breathNumber} value={breath.breathNumber}>Respiração {breath.breathNumber}</option>)}
                </select>
                <div className="grid gap-4 md:grid-cols-2">
                  <div ref={(element) => (chartRefs.current.individual_pressure = element)}>
                    <WaveformChart title="Pressão (cmH2O)" color="#0f172a" unit="cmH2O" dataKey="pressure" data={chartData} />
                  </div>
                  <div ref={(element) => (chartRefs.current.individual_flow = element)}>
                    <WaveformChart title="Fluxo (L/s)" color="#059669" unit="L/s" dataKey="flow" data={chartData} />
                  </div>
                  <div ref={(element) => (chartRefs.current.individual_volume = element)}>
                    <WaveformChart title="Volume (L)" color="#d97706" unit="L" dataKey="volume" data={chartData} />
                  </div>
                  <div ref={(element) => (chartRefs.current.individual_work = element)}>
                    <WaveformChart title="Trabalho acumulado" color="#7c3aed" unit="cmH2O.L" dataKey="work" data={chartData} />
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="overflow-x-auto rounded-lg border">
            <table className="min-w-full text-sm">
              <thead className="bg-slate-50 text-slate-500">
                <tr>
                  <th className="px-3 py-2 text-left">Bloco</th>
                  <th className="px-3 py-2 text-left">Respirações</th>
                  <th className="px-3 py-2 text-left">Pico fluxo</th>
                  <th className="px-3 py-2 text-left">Pico volume</th>
                  <th className="px-3 py-2 text-left">Trabalho</th>
                </tr>
              </thead>
              <tbody>
                {analysis.blocks.map((block) => (
                  <tr key={block.index} className="border-t">
                    <td className="px-3 py-2">Bloco {block.index}</td>
                    <td className="px-3 py-2">{block.breathRange}</td>
                    <td className="px-3 py-2">{block.meanPeakFlow.toFixed(2)} L/s</td>
                    <td className="px-3 py-2">{block.meanPeakVolume.toFixed(2)} L</td>
                    <td className="px-3 py-2">{block.meanTotalWork.toFixed(2)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {blockChartData.length > 1 && (
            <div className="space-y-2" ref={(element) => (chartRefs.current.block_evolution = element)}>
              <div className="text-sm font-semibold text-slate-700">Trabalho respiratório ao longo do teste</div>
              <div className="rounded-lg border bg-white p-3 shadow-sm">
                <ResponsiveContainer width="100%" height={240}>
                  <LineChart data={blockChartData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="block" tickFormatter={(value) => `#${value}`} />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Line type="monotone" dataKey="work" stroke="#f97316" name="Trabalho" dot={false} strokeWidth={2} isAnimationActive={false} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}
        </>
      )}

      {exportingCharts && <div className="rounded-lg border border-blue-200 bg-blue-50 p-3 text-sm text-blue-900">Exportando gráficos para impressão...</div>}
      {protocolValidationMessage && <div className="text-sm text-amber-700">{protocolValidationMessage}</div>}

      <div className="flex items-center justify-between pt-2">
        <button type="button" className="text-sm text-slate-600 hover:text-slate-800" onClick={onCancel}>Cancelar</button>
        <button type="button" className="inline-flex items-center rounded-md bg-green-600 px-3 py-2 text-white disabled:opacity-50" disabled={!summaryPreview || Boolean(protocolValidationMessage)} onClick={handleFinish}>
          Concluir Endurance <Check className="ml-1 h-4 w-4" />
        </button>
      </div>
    </div>
  );
}

const Metric = ({ label, value, emphasis = false }: { label: string; value: string; emphasis?: boolean }) => (
  <div className="rounded-lg border bg-white p-3 shadow-sm">
    <div className="text-xs uppercase text-slate-500">{label}</div>
    <div className={`text-2xl font-extrabold ${emphasis ? 'text-sky-700' : 'text-slate-900'}`}>{value}</div>
  </div>
);

const NumberField = ({ label, value, unit, onChange }: { label: string; value?: number | null; unit?: string; onChange: (value: number | null) => void }) => {
  const [draft, setDraft] = useState(value != null ? String(value) : '');
  useEffect(() => setDraft(value != null ? String(value) : ''), [value]);

  return (
    <label className="block text-sm font-medium text-slate-700">
      {label}
      <div className="mt-1 flex rounded-lg border border-slate-300 bg-white focus-within:border-sky-500">
        <input
          className="min-w-0 flex-1 rounded-l-lg px-3 py-2 outline-none"
          inputMode="decimal"
          value={draft}
          onChange={(event) => {
            const next = event.target.value;
            setDraft(next);
            if (!next.trim()) onChange(null);
            else {
              const parsed = toFiniteNumber(next);
              if (parsed != null) onChange(parsed);
            }
          }}
        />
        {unit && <span className="flex items-center border-l border-slate-200 px-2 text-xs text-slate-500">{unit}</span>}
      </div>
    </label>
  );
};

const TextField = ({ label, value, onChange }: { label: string; value: string; onChange: (value: string) => void }) => (
  <label className="block text-sm font-medium text-slate-700">
    {label}
    <input className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2" value={value} onChange={(event) => onChange(event.target.value)} />
  </label>
);

const SelectField = ({ label, value, options, onChange }: { label: string; value: string; options: Array<readonly [string, string]>; onChange: (value: string) => void }) => (
  <label className="block text-sm font-medium text-slate-700">
    {label}
    <select className="mt-1 w-full rounded-lg border border-slate-300 bg-white px-3 py-2" value={value} onChange={(event) => onChange(event.target.value)}>
      {options.map(([optionValue, optionLabel]) => <option key={optionValue || 'empty'} value={optionValue}>{optionLabel}</option>)}
    </select>
  </label>
);

const ReadOnlyField = ({ label, value }: { label: string; value: string }) => (
  <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm">
    <div className="text-xs text-slate-500">{label}</div>
    <div className="font-semibold text-slate-900">{value}</div>
  </div>
);
